import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

/**
 * Shared admin UI primitives. Kept deliberately small so new admin
 * pages can compose them without pulling in a heavier design system.
 * Styling matches the existing admin aesthetic (cream backgrounds,
 * forest accents, gold highlights where relevant).
 */

// ─── StatusPill ──────────────────────────────────────────────────────

export type PillTone =
  | "forest"
  | "sky"
  | "gold"
  | "red"
  | "bark"
  | "emerald";

const toneStyles: Record<PillTone, { bg: string; dot: string; text: string }> = {
  forest: {
    bg: "bg-forest-50",
    dot: "bg-forest-500",
    text: "text-forest-700",
  },
  sky: {
    bg: "bg-sky-50",
    dot: "bg-sky-500",
    text: "text-sky-700",
  },
  gold: {
    bg: "bg-gold-50",
    dot: "bg-gold-500",
    text: "text-gold-800",
  },
  red: {
    bg: "bg-red-50",
    dot: "bg-red-400",
    text: "text-red-700",
  },
  bark: {
    bg: "bg-bark-100",
    dot: "bg-bark-400",
    text: "text-bark-600",
  },
  emerald: {
    bg: "bg-emerald-50",
    dot: "bg-emerald-500",
    text: "text-emerald-700",
  },
};

export function StatusPill({
  tone,
  children,
}: {
  tone: PillTone;
  children: ReactNode;
}) {
  const style = toneStyles[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${style.bg} ${style.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
      {children}
    </span>
  );
}

// ─── SearchBar ───────────────────────────────────────────────────────

export function SearchBar({
  value,
  onChange,
  placeholder,
  autoFocus = false,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative max-w-sm">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-forest-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="paper-card w-full rounded-lg border border-forest-200 bg-white py-2 pl-9 pr-9 text-sm text-forest-900 placeholder:text-forest-400 focus:border-forest-500 focus:outline-none"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-forest-400 hover:bg-forest-50 hover:text-forest-600"
          aria-label="Clear search"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// Debounced hook for search inputs.
export function useDebouncedValue<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// ─── FilterToggle ────────────────────────────────────────────────────

/**
 * Small labelled checkbox used for "show X" filters (e.g. show revoked
 * licences, show internal users). Intended to sit next to a SearchBar
 * in a PageHeader's actions slot.
 */
export function FilterToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="paper-card inline-flex cursor-pointer items-center gap-2 rounded-lg border border-forest-200 bg-white px-3 py-2 text-xs font-medium text-forest-700 hover:bg-forest-50">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-forest-700"
      />
      {label}
    </label>
  );
}

// ─── Pagination ──────────────────────────────────────────────────────

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number; // zero-based
  pageSize: number;
  total: number;
  onPageChange: (next: number) => void;
}) {
  const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="flex items-center justify-between pt-4 text-xs text-forest-600">
      <p>
        Showing <span className="font-semibold">{from}</span>&ndash;
        <span className="font-semibold">{to}</span> of{" "}
        <span className="font-semibold">{total}</span>
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          className="inline-flex items-center gap-1 rounded-lg border border-forest-200 px-2.5 py-1.5 text-forest-700 hover:bg-forest-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Prev
        </button>
        <span className="tabular-nums">
          Page <span className="font-semibold">{page + 1}</span> of{" "}
          <span className="font-semibold">{maxPage + 1}</span>
        </span>
        <button
          onClick={() => onPageChange(Math.min(maxPage, page + 1))}
          disabled={page >= maxPage}
          className="inline-flex items-center gap-1 rounded-lg border border-forest-200 px-2.5 py-1.5 text-forest-700 hover:bg-forest-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next page"
        >
          Next
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─── AdminTable ──────────────────────────────────────────────────────

export interface AdminTableColumn<Row> {
  key: string;
  header: ReactNode;
  render: (row: Row) => ReactNode;
  align?: "left" | "right";
  width?: string;
}

export function AdminTable<Row>({
  columns,
  rows,
  loading,
  emptyMessage,
  rowKey,
}: {
  columns: AdminTableColumn<Row>[];
  rows: Row[];
  loading?: boolean;
  emptyMessage?: string;
  /** Return a stable unique key for each row. Defaults to `row.id`
   *  when present, otherwise the row index (fine for small tables,
   *  but supply this explicitly once the row set is reorderable). */
  rowKey?: (row: Row) => string | number;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest-300 border-t-forest-700" />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="paper-card rounded-xl border border-cream-300/60 bg-white px-6 py-10 text-center text-sm text-forest-500">
        {emptyMessage ?? "No rows."}
      </div>
    );
  }
  return (
    <div className="workspace-table overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-forest-200 text-forest-500">
            {columns.map((col) => (
              <th
                key={col.key}
                style={col.width ? { width: col.width } : undefined}
                className={`pb-3 pr-4 font-medium ${col.align === "right" ? "text-right" : ""}`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const key =
              rowKey
                ? rowKey(row)
                : ((row as { id?: string | number }).id ?? i);
            return (
              <tr key={key} className="border-b border-forest-100">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`py-3 pr-4 ${col.align === "right" ? "text-right" : ""}`}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── ConfirmModal ────────────────────────────────────────────────────

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  confirmTone = "red",
  requireTypedConfirm,
  onConfirm,
  onCancel,
  busy,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  confirmTone?: "red" | "forest";
  /** If set, the confirm button is disabled until the user types the
   *  exact string, character-for-character. Use for destructive or
   *  hard-to-undo actions. */
  requireTypedConfirm?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const [typed, setTyped] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTyped("");
      // focus on next tick so the modal mount completes first
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  if (!open) return null;

  const typedOk =
    !requireTypedConfirm || typed.trim() === requireTypedConfirm.trim();

  const confirmClasses =
    confirmTone === "red"
      ? "bg-red-600 hover:bg-red-700 text-white"
      : "bg-forest-900 hover:bg-forest-800 text-cream-100";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bark-900/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="font-display text-lg font-bold text-forest-900">
          {title}
        </h3>
        <div className="mt-2 text-sm text-forest-600">{description}</div>

        {requireTypedConfirm && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-forest-700">
              Type <code className="font-mono text-forest-900">{requireTypedConfirm}</code> to confirm
            </label>
            <input
              ref={inputRef}
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="paper-card mt-1.5 w-full rounded-lg border border-forest-200 bg-white px-3 py-2 font-mono text-sm text-forest-900 focus:border-forest-500 focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-forest-600 hover:bg-forest-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!typedOk || busy}
            className={`rounded-lg px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${confirmClasses}`}
          >
            {busy ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PageHeader ──────────────────────────────────────────────────────

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="workspace-heading mb-8 flex flex-col gap-4 border-b border-forest-800/15 pb-6 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="font-display text-2xl font-bold text-forest-900">
          {title}
        </h1>
        {description && (
          <p className="mt-1 max-w-2xl text-sm text-forest-500">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
